import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildSelfServiceApiUrl, unwrapArray, unwrapObject } from './self-service-api.utils';
import { readJson, writeJson } from './self-service-storage.utils';

@Injectable({
  providedIn: 'root',
})
export class SelfServiceChartService {
  private static readonly STORAGE_KEY = 'dvm.selfService.savedCharts';

  constructor(private readonly http: HttpClient) {}

  listChartTypes<T>(): Observable<T[]> {
    return this.http
      .get<unknown>(buildSelfServiceApiUrl('/api/dvm/self-service/chart-types'))
      .pipe(
        map((response) => unwrapArray<T>(response, ['items', 'data', 'results', 'chartTypes'])),
        catchError(() => of([])),
      );
  }

  listCharts<T>(): Observable<T[]> {
    return this.http
      .get<unknown>(buildSelfServiceApiUrl('/api/dvm/self-service/charts'))
      .pipe(
        map((response) => {
          const items = unwrapArray<T>(response, ['items', 'data', 'results', 'charts']);
          return items.length ? items : this.readLocal<T>();
        }),
        catchError(() => of(this.readLocal<T>())),
      );
  }

  getChart<T>(chartId: string): Observable<T | null> {
    return this.http
      .get<unknown>(buildSelfServiceApiUrl(`/api/dvm/self-service/charts/${encodeURIComponent(chartId)}`))
      .pipe(
        map((response) => unwrapObject<T>(response)),
        catchError(() => of(this.readLocal<T>().find((item: any) => item?.id === chartId) || null)),
      );
  }

  saveChart<T extends { id: string; datasetId?: string | null }>(chart: T): Observable<T> {
    const request = chart.id
      ? this.http.put<unknown>(
          buildSelfServiceApiUrl(`/api/dvm/self-service/charts/${encodeURIComponent(chart.id)}`),
          this.toBackendPayload(chart),
        )
      : this.http.post<unknown>(
          buildSelfServiceApiUrl('/api/dvm/self-service/charts'),
          this.toBackendPayload(chart),
        );

    return request.pipe(
      map((response) => {
        const saved = unwrapObject<Record<string, unknown>>(response) || {};
        return { ...chart, ...saved } as T;
      }),
      catchError(() => {
        const next = this.upsertLocal(chart);
        return of(next);
      }),
    );
  }

  deleteChart(chartId: string): Observable<void> {
    return this.http
      .delete<void>(buildSelfServiceApiUrl(`/api/dvm/self-service/charts/${encodeURIComponent(chartId)}`))
      .pipe(
        catchError(() => {
          this.writeLocal(this.readLocal().filter((item: any) => item?.id !== chartId));
          return of(void 0);
        }),
      );
  }

  replaceLocalCache<T>(charts: T[]): void {
    this.writeLocal(charts);
  }

  private toBackendPayload<T extends { dataset?: unknown; config?: unknown; datasetId?: string | null }>(chart: T): Record<string, unknown> {
    const payload = { ...chart } as Record<string, unknown>;
    delete payload['dataset'];

    const config = { ...(payload['config'] as Record<string, unknown> || {}) };
    if (chart.datasetId) {
      config['datasetId'] = chart.datasetId;
    }
    payload['config'] = config;
    payload['datasetId'] = chart.datasetId || null;
    return payload;
  }

  private readLocal<T>(): T[] {
    // Transparently reads compressed (new) and legacy plaintext (old) payloads.
    return readJson<T[]>(SelfServiceChartService.STORAGE_KEY, []);
  }

  private writeLocal<T>(charts: T[]): void {
    // Compresses before writing so large embedded datasets don't blow the
    // localStorage quota. Best-effort — failure is swallowed (this is only the
    // HTTP-save fallback cache).
    writeJson(SelfServiceChartService.STORAGE_KEY, charts);
  }

  private upsertLocal<T extends { id: string }>(chart: T): T {
    const current = this.readLocal<T>();
    const next = current.some((item) => item.id === chart.id)
      ? current.map((item) => (item.id === chart.id ? chart : item))
      : [chart, ...current];
    this.writeLocal(next);
    return chart;
  }
}
